// Sales Coach Réel — page liste-tableau (V1 roadmap Modjo-like).
// Pour chaque appel : type, prospect, date+durée, score, top force, top axe,
// CTA "S'entraîner". Toolbar avec filtres + recherche full-text + tri.
//
// Différence avec l'ancienne version (cards grid) : la liste-tableau permet
// au commercial/manager d'identifier rapidement un appel à débriefer en 1:1
// et de lancer une simulation ciblée en 1 clic sans passer par la page détail.

import React, { useEffect, useMemo, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import {
  Mic, Plus, Lock, Bookmark, AlertCircle, CheckCircle2, Loader2, FileText, Search, X,
  TrendingUp, TrendingDown, Target, Dumbbell, Sparkles, ChevronDown, ChevronUp, Star,
} from "lucide-react";
import { salesCoachApi } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { EmptyState } from "../components/ui/empty-state";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { PrepareTrainingFromCoachDialog, type CoachTrainingContext } from "../components/PrepareTrainingFromCoachDialog";
import type { RealCallContextType } from "../types";

interface Props {
  onCreateNew: () => void;
  onOpenDetail: (id: string) => void;
  // Lance un simulateur depuis le CTA "S'entraîner" — branché via App.tsx
  // (handleStartTrainingFromCoach qui pré-remplit via N8N).
  onStartTrainingForKey?: (simulatorKey: string) => void;
  onOpenLibrary?: () => void;
  // Retour vers HistoryPage avec un onglet pré-sélectionné — utilisé par la
  // barre de tabs "Sessions de simulation / Sales Coach Réel / Préparations RDV"
  // affichée en haut pour garder le contexte de navigation.
  onNavigateToHistory?: (initialTab?: "training" | "prep-meeting") => void;
  // Vue partielle pour l'embarquer dans un espace de travail (Sessions live) :
  // "stats" = stats + progression ; "list" = barre de filtres + liste ; "full" = tout.
  view?: "full" | "stats" | "list";
}

// Item enrichi côté front, miroir de la réponse API listMySessionsFiltered.
type Row = {
  id: string;
  contextType: RealCallContextType | null;
  contextProspectName: string | null;
  contextProspectCompany: string | null;
  scoreGlobal: number | null;
  scoreTier: string | null;
  analysisStatus: "pending" | "in_progress" | "completed" | "failed";
  isPrivate: boolean;
  keepIndefinitely: boolean;
  createdAt: string;
  audioDurationSeconds: number | null;
  inputType: "transcript" | "audio" | "video" | null;
  topStrengthTitle: string | null;
  topImprovementTitle: string | null;
  summary: string | null;
  scoreCoachability: number | null;
  speechMetrics: {
    talkRatioCommercialPct: number;
    speechRateCommercialWpm: number;
    openQuestionsCount: number;
  } | null;
  searchSnippets: string[] | null;
  searchMatchCount: number | null;
  isHighlighted: boolean;
  methodologyFramework: string | null;
  methodologyCoveredCount: number | null;
  methodologyTotalCount: number | null;
};

// Mapping contextType → clé i18n. Le label réel est résolu via t() au rendu.
const CONTEXT_LABEL_KEYS: Record<string, string> = {
  cold_call: "salesCoach.list.contextColdCall",
  discovery: "salesCoach.list.contextDiscovery",
  pitch: "salesCoach.list.contextPitch",
  negotiation: "salesCoach.list.contextNegotiation",
  customer_success: "salesCoach.list.contextCustomerSuccess",
  qualification: "salesCoach.list.contextQualification",
  generic: "salesCoach.list.contextGeneric",
};

// Mapping contextType → simulatorKey pour pré-sélection du simulateur dans
// le dialog "S'entraîner". Cohérent avec backend (criteria.service).
const CONTEXT_TO_SIMULATOR: Record<string, { key: string; label: string }> = {
  cold_call: { key: "PROSPECTOR", label: "Prospector" },
  discovery: { key: "DISCOVERY", label: "Discovery" },
  pitch: { key: "PITCH", label: "Pitch" },
  negotiation: { key: "NEGOTIATOR", label: "Negotiator" },
  customer_success: { key: "SATISFACTOR", label: "Satisfactor" },
  qualification: { key: "MEDDIC", label: "MEDDIC" },
  generic: { key: "DISCOVERY", label: "Discovery" },
};

type ScoreFilter = "all" | "excellent" | "good" | "needs_improvement" | "insufficient";
type PeriodFilter = "all" | "7d" | "30d" | "90d";

type ProgressionPoint = { weekStart: string; count: number; avgScore: number | null; avgCoachability: number | null };

export function SalesCoachPage({ onCreateNew, onOpenDetail, onStartTrainingForKey, onOpenLibrary, onNavigateToHistory, view = "full" }: Props) {
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // V2 chantier 4 : progression
  const [progression, setProgression] = useState<ProgressionPoint[]>([]);

  // Filtres UI
  const [search, setSearch] = useState("");
  const [period, setPeriod] = useState<PeriodFilter>("all");
  const [contextType, setContextType] = useState<string>("");
  const [scoreFilter, setScoreFilter] = useState<ScoreFilter>("all");
  const [sort, setSort] = useState<"date_desc" | "date_asc" | "score_desc" | "score_asc" | "duration_desc">("date_desc");
  // V3 — filtre "Épinglés uniquement" (Library)
  const [highlightedOnly, setHighlightedOnly] = useState(false);
  // V3 — filtres méthodo (framework + couverture min %)
  const [methodologyFramework, setMethodologyFramework] = useState<string>("");
  const [methodologyMinCoverage, setMethodologyMinCoverage] = useState<string>("");

  // Dialog "S'entraîner"
  const [trainingDialogCtx, setTrainingDialogCtx] = useState<CoachTrainingContext | null>(null);

  // V2.1 — snippets de recherche : une seule ligne expandée à la fois
  const [expandedSnippetsId, setExpandedSnippetsId] = useState<string | null>(null);

  // Debounced search : 400ms après dernière frappe
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(id);
  }, [search]);

  // Fetch liste à chaque changement de filtre
  useEffect(() => {
    setLoading(true);
    setError(null);

    const fromDate = (() => {
      if (period === "all") return undefined;
      const days = period === "7d" ? 7 : period === "30d" ? 30 : 90;
      const d = new Date();
      d.setDate(d.getDate() - days);
      return d.toISOString();
    })();

    const { minScore, maxScore } = (() => {
      switch (scoreFilter) {
        case "excellent": return { minScore: 80 };
        case "good": return { minScore: 60, maxScore: 79 };
        case "needs_improvement": return { minScore: 40, maxScore: 59 };
        case "insufficient": return { maxScore: 39 };
        default: return {};
      }
    })();

    salesCoachApi
      .listMySessionsFiltered({
        search: debouncedSearch || undefined,
        fromDate,
        contextType: contextType || undefined,
        minScore,
        maxScore,
        sort,
        highlightedOnly: highlightedOnly || undefined,
        methodologyFramework: methodologyFramework || undefined,
        methodologyMinCoveragePct: methodologyMinCoverage ? Number(methodologyMinCoverage) : undefined,
        limit: 100,
      })
      .then((data) => setRows(data as Row[]))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [debouncedSearch, period, contextType, scoreFilter, sort, highlightedOnly, methodologyFramework, methodologyMinCoverage]);

  // Progression : chargée une fois au mount (12 dernières semaines, indépendant des filtres)
  useEffect(() => {
    salesCoachApi.getMyProgression(12).then(setProgression).catch(() => setProgression([]));
  }, []);

  // Stats rapides en haut (calculées sur les rows visibles — sera amélioré V2
  // avec une route stats dédiée si besoin de stats globales).
  const stats = useMemo(() => {
    const completed = rows.filter((r) => r.analysisStatus === "completed" && r.scoreGlobal != null);
    const avg = completed.length > 0
      ? Math.round(completed.reduce((s, r) => s + (r.scoreGlobal ?? 0), 0) / completed.length)
      : null;
    return { total: rows.length, avgScore: avg, completedCount: completed.length };
  }, [rows]);

  const hasActiveFilters = !!debouncedSearch || period !== "all" || contextType !== "" || scoreFilter !== "all" || !!methodologyFramework || !!methodologyMinCoverage;

  // V3 — Toggle highlight (épingler/désépingler comme exemple)
  async function toggleHighlight(row: Row) {
    try {
      const next = !row.isHighlighted;
      await salesCoachApi.toggleSessionHighlight(row.id, next);
      setRows((prev) => prev.map((r) => r.id === row.id ? { ...r, isHighlighted: next } : r));
    } catch (err) {
      alert(err instanceof Error ? err.message : t("salesCoach.list.errorGeneric"));
    }
  }

  function openTrainingDialog(row: Row) {
    const sim = CONTEXT_TO_SIMULATOR[row.contextType ?? "generic"] ?? CONTEXT_TO_SIMULATOR.generic!;
    setTrainingDialogCtx({
      simulatorKey: sim.key,
      simulatorLabel: sim.label,
      axeTitle: row.topImprovementTitle ?? undefined,
      contextNotes: row.summary ?? undefined,
      scoreGlobal: row.scoreGlobal ?? undefined,
      prospectName: row.contextProspectName,
      prospectCompany: row.contextProspectCompany,
      // Chantier 2.2 — id du débrief source pour création du cas privé persistant
      realCallSessionId: row.id,
    });
  }

  return (
    <div className="space-y-6">
      {/* Barre de tabs Historique (cohérence avec HistoryPage) — Sales Coach Réel actif */}
      {onNavigateToHistory && (
        <div className="flex gap-2 border-b flex-wrap">
          <button
            onClick={() => onNavigateToHistory("training")}
            className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 border-b-2 border-transparent transition-colors"
          >
            {t("history.tabSimulationSessions")}
          </button>
          <button
            className="px-4 py-2 text-sm font-semibold border-b-2 border-current"
            style={{ color: "#384872" }}
            aria-current="page"
          >
            <span className="inline-flex items-center gap-1.5">
              {t("history.tabSalesCoach")}
            </span>
          </button>
          <button
            onClick={() => onNavigateToHistory("prep-meeting")}
            className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 border-b-2 border-transparent transition-colors"
          >
            <span className="inline-flex items-center gap-1.5">
              {t("history.tabPrepMeeting")}
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full uppercase" style={{ background: "#988EF2", color: "white" }}>
                {t("history.newBadge")}
              </span>
            </span>
          </button>
        </div>
      )}

      {/* Header — masqué quand la page est embarquée en vue partielle */}
      {view === "full" && (
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="h-12 w-12 rounded-full flex items-center justify-center" style={{ background: "#988EF2" }}>
              <Mic className="h-6 w-6 text-white" />
            </div>
            <h1 className="text-3xl font-bold" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>
              {t("salesCoach.list.title")}
            </h1>
            <Badge style={{ background: "#988EF2", color: "white" }}>{t("salesCoach.list.alphaBadge")}</Badge>
          </div>
          <p className="mt-2 max-w-3xl text-sm md:text-base" style={{ color: "#4B5573" }}>
            {t("salesCoach.list.intro")}
          </p>
        </div>
        <div className="flex items-start gap-2 flex-wrap">
          {onOpenLibrary && (
            <div className="flex flex-col items-end gap-0.5">
              <Button
                disabled
                size="lg"
                variant="outline"
                className="cursor-not-allowed opacity-60"
                style={{ borderColor: "#CBD5E1", color: "#94A3B8", background: "#F8FAFC" }}
                title={t("salesCoach.list.comingSoon")}
              >
                <Target className="h-5 w-5 mr-2" />
                {t("salesCoach.list.myObjections")}
              </Button>
              <span className="text-[10px] italic" style={{ color: "#94A3B8" }}>
                {t("salesCoach.list.comingSoonShort")}
              </span>
            </div>
          )}
          <Button onClick={onCreateNew} size="lg" style={{ background: "#384872", color: "white" }}>
            <Plus className="h-5 w-5 mr-2" />
            {t("salesCoach.list.newSession")}
          </Button>
        </div>
      </div>
      )}

      {/* Stats rapides — visibles si au moins 1 session complétée (pas en vue liste) */}
      {view !== "list" && !loading && stats.completedCount > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <StatCard label={t("salesCoach.list.statSessions")} value={String(stats.total)} icon={<FileText className="h-5 w-5" />} color="#384872" />
          <StatCard label={t("salesCoach.list.statAvgScore")} value={stats.avgScore != null ? `${stats.avgScore}/100` : t("salesCoach.list.dash")} icon={<Target className="h-5 w-5" />} color="#5A4BBE" />
          <StatCard label={t("salesCoach.list.statDebriefed")} value={`${stats.completedCount}/${stats.total}`} icon={<CheckCircle2 className="h-5 w-5" />} color="#1E7A3C" />
        </div>
      )}

      {/* V2 chantier 4 — Widget progression (12 semaines) — pas en vue liste */}
      {view !== "list" && progression.length >= 2 && <ProgressionWidget data={progression} />}

      {/* Filtres + liste — masqués en vue « stats » */}
      {view !== "stats" && (
      <>
      {/* Toolbar filtres */}
      <Card className="p-4">
        <div className="space-y-3">
          {/* Recherche full-text — pleine largeur */}
          <div className="min-w-0">
            <Label htmlFor="search" className="text-xs font-medium flex items-center gap-1.5" style={{ color: "#4B5573" }}>
              <span>{t("salesCoach.list.searchLabel")}</span>
              {parseSearchUI(search).exact && (
                <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider" style={{ background: "#FEF3C7", color: "#854D0E" }}>
                  {t("salesCoach.list.searchExactMode")}
                </span>
              )}
            </Label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none" style={{ color: "#9CA3AF" }} />
              <Input
                id="search"
                type="search"
                inputMode="search"
                autoComplete="off"
                autoCorrect="off"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("salesCoach.list.searchPlaceholder")}
                className="pl-9 pr-9 h-11"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-gray-100"
                  aria-label={t("salesCoach.list.searchClear")}
                >
                  <X className="h-4 w-4" style={{ color: "#6B7280" }} />
                </button>
              )}
            </div>
          </div>

          {/* Filtres — grille responsive (2 col mobile, 3 tablette, 6 desktop) */}
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
          {/* Période */}
          <FilterSelect
            label={t("salesCoach.list.filterPeriod")}
            value={period}
            onChange={(v) => setPeriod(v as PeriodFilter)}
            options={[
              { value: "all", label: t("salesCoach.list.periodAll") },
              { value: "7d", label: t("salesCoach.list.period7d") },
              { value: "30d", label: t("salesCoach.list.period30d") },
              { value: "90d", label: t("salesCoach.list.period90d") },
            ]}
          />

          {/* Type */}
          <FilterSelect
            label={t("salesCoach.list.filterCallType")}
            value={contextType}
            onChange={setContextType}
            options={[
              { value: "", label: t("salesCoach.list.callTypeAll") },
              ...Object.entries(CONTEXT_LABEL_KEYS).map(([value, key]) => ({ value, label: t(key) })),
            ]}
          />

          {/* Score */}
          <FilterSelect
            label={t("salesCoach.list.filterScore")}
            value={scoreFilter}
            onChange={(v) => setScoreFilter(v as ScoreFilter)}
            options={[
              { value: "all", label: t("salesCoach.list.scoreAll") },
              { value: "excellent", label: t("salesCoach.list.scoreExcellent") },
              { value: "good", label: t("salesCoach.list.scoreGood") },
              { value: "needs_improvement", label: t("salesCoach.list.scoreNeedsImprovement") },
              { value: "insufficient", label: t("salesCoach.list.scoreInsufficient") },
            ]}
          />

          {/* Tri */}
          <FilterSelect
            label={t("salesCoach.list.filterSort")}
            value={sort}
            onChange={(v) => setSort(v as typeof sort)}
            options={[
              { value: "date_desc", label: t("salesCoach.list.sortDateDesc") },
              { value: "date_asc", label: t("salesCoach.list.sortDateAsc") },
              { value: "score_desc", label: t("salesCoach.list.sortScoreDesc") },
              { value: "score_asc", label: t("salesCoach.list.sortScoreAsc") },
              { value: "duration_desc", label: t("salesCoach.list.sortDurationDesc") },
            ]}
          />

          {/* V3 — Méthodo */}
          <FilterSelect
            label={t("salesCoach.list.filterMethodology")}
            value={methodologyFramework}
            onChange={setMethodologyFramework}
            options={[
              { value: "", label: t("salesCoach.list.methodologyAll") },
              { value: "MEDDIC", label: "MEDDIC" },
              { value: "BANT", label: "BANT" },
              { value: "SPICED", label: "SPICED" },
              { value: "GPCT", label: "GPCT" },
            ]}
          />
          <FilterSelect
            label={t("salesCoach.list.filterMinCoverage")}
            value={methodologyMinCoverage}
            onChange={setMethodologyMinCoverage}
            options={[
              { value: "", label: t("salesCoach.list.coverageAll") },
              { value: "75", label: t("salesCoach.list.coverage75") },
              { value: "50", label: t("salesCoach.list.coverage50") },
              { value: "25", label: t("salesCoach.list.coverage25") },
            ]}
          />
          </div>
        </div>
        {/* Astuce de recherche (sous la rangée pour ne pas casser l'alignement vertical desktop) */}
        <p className="mt-2 text-[11px]" style={{ color: "#9CA3AF" }}>
          <Trans i18nKey="salesCoach.list.searchTip" components={[<code />]} />
        </p>
        {/* V3 — Toggle "Épinglés uniquement" (Library) */}
        <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
          <button
            onClick={() => setHighlightedOnly(!highlightedOnly)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors"
            style={{
              background: highlightedOnly ? "#FEF08A" : "#F3F4F6",
              color: highlightedOnly ? "#854D0E" : "#4B5573",
            }}
          >
            <Star className="h-3.5 w-3.5" fill={highlightedOnly ? "currentColor" : "none"} />
            {t("salesCoach.list.highlightedOnly")}
          </button>
          {(hasActiveFilters || highlightedOnly) && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setSearch(""); setPeriod("all"); setContextType(""); setScoreFilter("all"); setHighlightedOnly(false); setMethodologyFramework(""); setMethodologyMinCoverage(""); }}
                className="text-xs underline"
                style={{ color: "#5A4BBE" }}
              >
                {t("salesCoach.list.resetFilters")}
              </button>
              <span className="text-xs" style={{ color: "#9CA3AF" }}>
                {t("salesCoach.list.resultCount", { count: rows.length })}
              </span>
            </div>
          )}
        </div>
      </Card>

      {/* États : loading / error / empty / data */}
      {loading && (
        <div className="text-center py-12" style={{ color: "#4B5573" }}>
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3" />
          {t("salesCoach.list.loading")}
        </div>
      )}

      {error && (
        <Card className="p-4 border-2" style={{ borderColor: "#F49390", background: "#FFF5F5" }}>
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 mt-0.5" style={{ color: "#C0423F" }} />
            <div>
              <div className="font-semibold" style={{ color: "#C0423F" }}>{t("salesCoach.list.errorTitle")}</div>
              <div className="text-sm mt-1" style={{ color: "#4B5573" }}>{error}</div>
            </div>
          </div>
        </Card>
      )}

      {!loading && !error && rows.length === 0 && !hasActiveFilters && (
        <EmptyState
          icon="🎙️"
          iconGradient="var(--grad-coach)"
          title={t("salesCoach.list.emptyTitle")}
          description={t("salesCoach.list.emptyDescription")}
          action={(
            <Button onClick={onCreateNew} style={{ backgroundImage: "var(--grad-coach)", color: "#fff" }}>
              <Plus className="h-4 w-4 mr-2" />
              {t("salesCoach.list.emptyCta")}
            </Button>
          )}
        />
      )}

      {!loading && !error && rows.length === 0 && hasActiveFilters && (
        <EmptyState
          icon="🔍"
          iconGradient="var(--grad-coach)"
          title={t("salesCoach.list.noResultsTitle")}
          description={t("salesCoach.list.noResultsDescription")}
        />
      )}

      {/* Liste — table desktop, cards mobile */}
      {!loading && !error && rows.length > 0 && (
        <>
          {/* Desktop (≥ lg) : tableau qui tient sans scroll horizontal. En dessous → cartes. */}
          <div className="hidden lg:block">
            <Card>
              <table className="w-full table-fixed text-sm">
                <colgroup>
                  <col style={{ width: "12%" }} />
                  <col style={{ width: "13%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "11%" }} />
                  <col style={{ width: "16%" }} />
                  <col style={{ width: "17%" }} />
                  <col style={{ width: "12%" }} />
                </colgroup>
                <thead style={{ background: "#F8F7FF" }}>
                  <tr style={{ color: "#4B5573" }} className="text-left text-xs uppercase tracking-wider">
                    <th className="px-4 py-3 font-semibold">{t("salesCoach.list.columnType")}</th>
                    <th className="px-4 py-3 font-semibold">{t("salesCoach.list.columnProspect")}</th>
                    <th className="px-4 py-3 font-semibold">{t("salesCoach.list.columnDateDuration")}</th>
                    <th className="px-4 py-3 font-semibold">{t("salesCoach.list.columnScore")}</th>
                    <th className="px-4 py-3 font-semibold">{t("salesCoach.list.columnSpeech")}</th>
                    <th className="px-4 py-3 font-semibold">{t("salesCoach.list.columnStrength")}</th>
                    <th className="px-4 py-3 font-semibold">{t("salesCoach.list.columnImprovement")}</th>
                    <th className="px-4 py-3 font-semibold text-right">{t("salesCoach.list.columnAction")}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                  const hasSnippets = Array.isArray(r.searchSnippets) && r.searchSnippets.length > 0;
                  const isExpanded = expandedSnippetsId === r.id;
                  return (
                  <React.Fragment key={r.id}>
                    <tr
                      className="border-t cursor-pointer hover:bg-gray-50 transition-colors"
                      style={{ borderColor: "#E5E7EB" }}
                      onClick={() => onOpenDetail(r.id)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge style={{ background: "#A6D3F9", color: "#384872" }}>
                            {CONTEXT_LABEL_KEYS[r.contextType ?? "generic"]
                              ? t(CONTEXT_LABEL_KEYS[r.contextType ?? "generic"]!)
                              : r.contextType ?? t("salesCoach.list.dash")}
                          </Badge>
                          {r.methodologyFramework && r.methodologyTotalCount != null && r.methodologyTotalCount > 0 && (
                            <span
                              className="text-[10px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wider"
                              style={{
                                background: methoCoverageBg(r.methodologyCoveredCount ?? 0, r.methodologyTotalCount),
                                color: methoCoverageColor(r.methodologyCoveredCount ?? 0, r.methodologyTotalCount),
                              }}
                              title={t("salesCoach.list.methoCoverageTooltip", {
                                framework: r.methodologyFramework,
                                covered: r.methodologyCoveredCount,
                                total: r.methodologyTotalCount,
                              })}
                            >
                              {r.methodologyFramework} {r.methodologyCoveredCount ?? 0}/{r.methodologyTotalCount}
                            </span>
                          )}
                          {hasSnippets && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setExpandedSnippetsId(isExpanded ? null : r.id); }}
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold transition-colors hover:opacity-80"
                              style={{ background: "#FEF3C7", color: "#926B00" }}
                              title={r.searchSnippets!.length === 1
                                ? r.searchSnippets![0]
                                : t("salesCoach.list.snippetsButtonTitleMany", { count: r.searchMatchCount ?? 0 })}
                              aria-label={t("salesCoach.list.snippetsAriaLabel", { count: r.searchMatchCount ?? 0, search: debouncedSearch })}
                            >
                              <Search className="h-3 w-3" />
                              {r.searchMatchCount ?? r.searchSnippets!.length}
                              {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium" style={{ color: "#384872" }}>
                          {r.contextProspectName || t("salesCoach.list.prospectAnonymous")}
                        </div>
                        {r.contextProspectCompany && (
                          <div className="text-xs" style={{ color: "#6B7280" }}>{r.contextProspectCompany}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs" style={{ color: "#4B5573" }}>
                        <div>{formatRelativeDate(r.createdAt, t, i18n.language)}</div>
                        {r.audioDurationSeconds != null && (
                          <div style={{ color: "#9CA3AF" }}>{formatDuration(r.audioDurationSeconds, t)}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">{renderScoreCell(r, t)}</td>
                      <td className="px-4 py-3">{renderSpeechCell(r, t)}</td>
                      <td className="px-4 py-3 max-w-xs">
                        {r.topStrengthTitle ? (
                          <div className="flex items-start gap-1.5 text-xs" style={{ color: "#1E7A3C" }}>
                            <TrendingUp className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                            <span className="line-clamp-2">{r.topStrengthTitle}</span>
                          </div>
                        ) : <span className="text-xs" style={{ color: "#D1D5DB" }}>{t("salesCoach.list.dash")}</span>}
                      </td>
                      <td className="px-4 py-3 max-w-xs">
                        {r.topImprovementTitle ? (
                          <div className="flex items-start gap-1.5 text-xs" style={{ color: "#C0423F" }}>
                            <TrendingDown className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                            <span className="line-clamp-2">{r.topImprovementTitle}</span>
                          </div>
                        ) : <span className="text-xs" style={{ color: "#D1D5DB" }}>{t("salesCoach.list.dash")}</span>}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {r.analysisStatus === "completed" && onStartTrainingForKey ? (
                          <div className="flex flex-col items-end gap-1">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={(e) => { e.stopPropagation(); toggleHighlight(r); }}
                                className="p-2.5 rounded-full hover:bg-yellow-50 transition-colors select-none"
                                title={r.isHighlighted ? t("salesCoach.list.unpin") : t("salesCoach.list.pinAsExample")}
                                aria-label={r.isHighlighted ? t("salesCoach.list.unpin") : t("salesCoach.list.pin")}
                              >
                                <Star
                                  className="h-4 w-4"
                                  fill={r.isHighlighted ? "#F59E0B" : "none"}
                                  style={{ color: r.isHighlighted ? "#F59E0B" : "#9CA3AF" }}
                                />
                              </button>
                              <Button
                                size="icon"
                                variant="outline"
                                onClick={(e) => { e.stopPropagation(); openTrainingDialog(r); }}
                                className="h-9 w-9 shrink-0"
                                title={t("salesCoach.list.train")}
                                aria-label={t("salesCoach.list.train")}
                                style={{ borderColor: "#988EF2", color: "#5A4BBE" }}
                              >
                                <Dumbbell className="h-4 w-4" />
                              </Button>
                            </div>
                            {r.scoreCoachability != null && r.scoreCoachability > 0 && (
                              <span className="text-[10px] font-semibold" style={{ color: "#5A4BBE" }} title={t("salesCoach.list.coachabilityTooltip")}>
                                {t("salesCoach.list.coachabilityToReplay", { count: r.scoreCoachability })}
                              </span>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 justify-end">
                            {r.isPrivate && <Lock className="h-4 w-4" style={{ color: "#9CA3AF" }} />}
                            {r.keepIndefinitely && <Bookmark className="h-4 w-4" style={{ color: "#384872" }} />}
                          </div>
                        )}
                      </td>
                    </tr>
                    {/* V2.1 — Ligne expand snippets de recherche */}
                    {isExpanded && hasSnippets && (
                      <tr style={{ background: "#FFFBEB", borderColor: "#FEF3C7" }} className="border-t">
                        <td colSpan={8} className="px-4 py-3">
                          <SnippetsPanel
                            snippets={r.searchSnippets!}
                            totalCount={r.searchMatchCount ?? r.searchSnippets!.length}
                            search={debouncedSearch}
                            onOpen={() => onOpenDetail(r.id)}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  );
                  })}
                </tbody>
              </table>
            </Card>
          </div>

          {/* Tablette + mobile (< lg) : cards (touch friendly) */}
          <div className="lg:hidden space-y-3">
            {rows.map((r) => {
            const hasSnippets = Array.isArray(r.searchSnippets) && r.searchSnippets.length > 0;
            const isExpanded = expandedSnippetsId === r.id;
            return (
              <Card
                key={r.id}
                className="p-4 cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => onOpenDetail(r.id)}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge style={{ background: "#A6D3F9", color: "#384872" }}>
                      {CONTEXT_LABEL_KEYS[r.contextType ?? "generic"]
                        ? t(CONTEXT_LABEL_KEYS[r.contextType ?? "generic"]!)
                        : r.contextType ?? t("salesCoach.list.dash")}
                    </Badge>
                    {r.methodologyFramework && r.methodologyTotalCount != null && r.methodologyTotalCount > 0 && (
                      <span
                        className="text-[10px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wider"
                        style={{
                          background: methoCoverageBg(r.methodologyCoveredCount ?? 0, r.methodologyTotalCount),
                          color: methoCoverageColor(r.methodologyCoveredCount ?? 0, r.methodologyTotalCount),
                        }}
                      >
                        {r.methodologyFramework} {r.methodologyCoveredCount ?? 0}/{r.methodologyTotalCount}
                      </span>
                    )}
                    {hasSnippets && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setExpandedSnippetsId(isExpanded ? null : r.id); }}
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold transition-colors"
                        style={{ background: "#FEF3C7", color: "#926B00" }}
                        aria-label={t("salesCoach.list.snippetsAriaLabelMobile")}
                      >
                        <Search className="h-3 w-3" />
                        {r.searchMatchCount ?? r.searchSnippets!.length}
                        {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                      </button>
                    )}
                  </div>
                  {renderScoreCell(r, t)}
                </div>
                <div className="font-semibold text-sm mb-0.5" style={{ color: "#384872" }}>
                  {r.contextProspectName || t("salesCoach.list.prospectAnonymous")}
                  {r.contextProspectCompany && (
                    <span className="font-normal text-xs ml-1" style={{ color: "#6B7280" }}>· {r.contextProspectCompany}</span>
                  )}
                </div>
                <div className="text-xs mb-2" style={{ color: "#9CA3AF" }}>
                  {formatRelativeDate(r.createdAt, t, i18n.language)}
                  {r.audioDurationSeconds != null && ` · ${formatDuration(r.audioDurationSeconds, t)}`}
                </div>
                {r.speechMetrics && r.analysisStatus === "completed" && (
                  <div className="flex items-center gap-3 text-xs mb-2 flex-wrap" style={{ color: "#4B5573" }}>
                    <span>🎤 {r.speechMetrics.talkRatioCommercialPct}%</span>
                    <span>⚡ {t("salesCoach.list.speechRateUnit", { count: r.speechMetrics.speechRateCommercialWpm })}</span>
                    <span>❓ {r.speechMetrics.openQuestionsCount}</span>
                    {r.scoreCoachability != null && r.scoreCoachability > 0 && (
                      <span className="ml-auto font-semibold" style={{ color: "#5A4BBE" }}>
                        {t("salesCoach.list.coachabilityMoment", { count: r.scoreCoachability })}
                      </span>
                    )}
                  </div>
                )}
                {r.topImprovementTitle && (
                  <div className="flex items-start gap-1.5 text-xs mb-2" style={{ color: "#C0423F" }}>
                    <TrendingDown className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <span className="line-clamp-2">{r.topImprovementTitle}</span>
                  </div>
                )}
                {r.analysisStatus === "completed" && onStartTrainingForKey && (
                  <div className="flex items-center gap-2 mt-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleHighlight(r); }}
                      className="p-2 rounded-full hover:bg-yellow-50 transition-colors shrink-0"
                      aria-label={r.isHighlighted ? t("salesCoach.list.unpin") : t("salesCoach.list.pin")}
                    >
                      <Star
                        className="h-4 w-4"
                        fill={r.isHighlighted ? "#F59E0B" : "none"}
                        style={{ color: r.isHighlighted ? "#F59E0B" : "#9CA3AF" }}
                      />
                    </button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => { e.stopPropagation(); openTrainingDialog(r); }}
                      className="flex-1"
                      style={{ borderColor: "#988EF2", color: "#5A4BBE" }}
                    >
                      <Dumbbell className="h-3.5 w-3.5 mr-1.5" />
                      {t("salesCoach.list.trainOnSituation")}
                    </Button>
                  </div>
                )}
                {/* V2.1 — Panel snippets de recherche (mobile) */}
                {isExpanded && hasSnippets && (
                  <div onClick={(e) => e.stopPropagation()} className="mt-3 -mx-4 -mb-4 px-4 py-3 rounded-b-2xl" style={{ background: "#FFFBEB", borderTop: "1px solid #FEF3C7" }}>
                    <SnippetsPanel
                      snippets={r.searchSnippets!}
                      totalCount={r.searchMatchCount ?? r.searchSnippets!.length}
                      search={debouncedSearch}
                      onOpen={() => onOpenDetail(r.id)}
                    />
                  </div>
                )}
              </Card>
            );
            })}
          </div>
        </>
      )}
      </>
      )}

      {/* Dialog "S'entraîner" — réutilise le composant existant (cohérent avec
          le bouton équivalent dans SalesCoachDetailPage). */}
      <PrepareTrainingFromCoachDialog
        open={!!trainingDialogCtx}
        context={trainingDialogCtx}
        onClose={() => setTrainingDialogCtx(null)}
        onLaunch={async (payload) => {
          // Chantier 2.2 — persist realCallSessionId pour App.tsx → coachingBrief BDD
          // + détails B2B Sales (industry/companySize/customIndustry) si applicables.
          localStorage.setItem("bizcoach_pending_coaching_context", JSON.stringify({
            simulatorKey: payload.simulatorKey,
            offerId: payload.offerId,
            personaId: payload.personaId,
            focus: payload.focus,
            difficulty: payload.difficulty,
            industry: payload.industry,
            companySize: payload.companySize,
            customIndustry: payload.customIndustry,
            contextId: payload.contextId,
            realCallSessionId: trainingDialogCtx?.realCallSessionId,
            createdAt: Date.now(),
          }));
          setTrainingDialogCtx(null);
          onStartTrainingForKey?.(payload.simulatorKey);
        }}
      />
    </div>
  );
}

// ============================================================
// Sous-composants
// ============================================================

// V2.1 — Panel des snippets de recherche, affiché en expand sous la ligne.
// Surligne le mot-clé dans chaque extrait (insensible casse + accents).
function SnippetsPanel({
  snippets, totalCount, search, onOpen,
}: {
  snippets: string[];
  totalCount: number;
  search: string;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  const remaining = totalCount - snippets.length;
  return (
    <div className="text-xs space-y-2">
      <div className="font-semibold uppercase tracking-wider" style={{ color: "#926B00" }}>
        {t("salesCoach.list.snippetsHeader", { count: snippets.length, search })}
        {remaining > 0 && (
          <span className="font-normal">
            {t("salesCoach.list.snippetsRemaining", { count: remaining })}
          </span>
        )}
      </div>
      <div className="space-y-1.5">
        {snippets.map((s, i) => (
          <div
            key={i}
            className="rounded-md px-2.5 py-2 leading-relaxed"
            style={{ background: "white", color: "#384872" }}
            dangerouslySetInnerHTML={{ __html: highlightSearchTerm(s, search) }}
          />
        ))}
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onOpen(); }}
        className="font-semibold hover:underline"
        style={{ color: "#5A4BBE" }}
      >
        {t("salesCoach.list.viewFullTranscript")}
      </button>
    </div>
  );
}

// Parse la query : guillemets → mode exact (word boundaries).
// Doit refléter parseSearchQuery côté backend pour cohérence highlight.
function parseSearchUI(raw: string): { term: string; exact: boolean } {
  const trimmed = raw.trim();
  const matchExact = trimmed.match(/^["“«](.+)["”»]$/) || trimmed.match(/^'(.+)'$/);
  if (matchExact && matchExact[1]) return { term: matchExact[1].trim(), exact: true };
  return { term: trimmed, exact: false };
}

// Surligne toutes les occurrences du terme de recherche dans le snippet.
// Insensible à la casse et aux accents. Échappe l'HTML pour éviter les XSS.
// Si query entre guillemets → match avec word boundaries.
function highlightSearchTerm(text: string, search: string): string {
  const parsed = parseSearchUI(search);
  const term = parsed.term;
  if (!term) return escapeHtml(text);

  const norm = (s: string) => s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
  const normText = norm(text);
  const normTerm = norm(term);

  const positions: Array<[number, number]> = [];
  if (parsed.exact) {
    const escaped = normTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp(`\\b${escaped}\\b`, "g");
    let m: RegExpExecArray | null;
    while ((m = re.exec(normText)) !== null) {
      positions.push([m.index, m.index + m[0].length]);
      if (m.index === re.lastIndex) re.lastIndex++;
    }
  } else {
    let idx = 0;
    while ((idx = normText.indexOf(normTerm, idx)) !== -1) {
      positions.push([idx, idx + normTerm.length]);
      idx += normTerm.length;
    }
  }
  if (positions.length === 0) return escapeHtml(text);

  let html = "";
  let last = 0;
  for (const [start, end] of positions) {
    html += escapeHtml(text.slice(last, start));
    html += `<mark style="background:#FEF08A;color:#854D0E;padding:0 2px;border-radius:2px;font-weight:600">${escapeHtml(text.slice(start, end))}</mark>`;
    last = end;
  }
  html += escapeHtml(text.slice(last));
  return html;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// V2 chantier 4 — Widget de progression : sparkline SVG du score moyen +
// delta semaine en cours vs précédente + gamification (cap "X appels ce mois").
function ProgressionWidget({ data }: { data: ProgressionPoint[] }) {
  const { t } = useTranslation();
  // Calcule delta : moyenne 4 dernières semaines vs 4 précédentes
  const last4 = data.slice(-4).filter((p) => p.avgScore != null);
  const prev4 = data.slice(-8, -4).filter((p) => p.avgScore != null);
  const avgLast = last4.length > 0 ? last4.reduce((s, p) => s + (p.avgScore ?? 0), 0) / last4.length : null;
  const avgPrev = prev4.length > 0 ? prev4.reduce((s, p) => s + (p.avgScore ?? 0), 0) / prev4.length : null;
  const delta = avgLast != null && avgPrev != null ? Math.round(avgLast - avgPrev) : null;

  const totalThisMonth = data.slice(-4).reduce((s, p) => s + p.count, 0);

  // Sparkline SVG : 240×48, ligne + points sur les semaines avec score
  const scorePoints = data.map((p) => p.avgScore).filter((s): s is number => s != null);
  const min = scorePoints.length > 0 ? Math.min(...scorePoints, 40) : 40;
  const max = scorePoints.length > 0 ? Math.max(...scorePoints, 80) : 80;
  const range = Math.max(max - min, 10);
  const W = 240, H = 48, PAD = 4;
  const stepX = data.length > 1 ? (W - 2 * PAD) / (data.length - 1) : 0;
  const yFor = (s: number) => H - PAD - ((s - min) / range) * (H - 2 * PAD);
  const pathD = data
    .map((p, i) => {
      if (p.avgScore == null) return null;
      const x = PAD + i * stepX;
      const y = yFor(p.avgScore);
      return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .filter(Boolean)
    .join(" ");

  return (
    <Card className="p-4">
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full flex items-center justify-center text-white shrink-0" style={{ background: "#5A4BBE" }}>
            <TrendingUp className="h-5 w-5" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider font-semibold" style={{ color: "#6B7280" }}>
              {t("salesCoach.list.progressionTitle")}
            </div>
            <div className="font-bold text-base" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>
              {t("salesCoach.list.progressionSummary", { count: totalThisMonth })}
            </div>
          </div>
        </div>

        {/* Sparkline */}
        {pathD && (
          <div className="flex-1 min-w-[200px] flex items-center gap-3">
            <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} aria-label={t("salesCoach.list.progressionSparkLabel")}>
              {/* Zone gradient sous la courbe */}
              <defs>
                <linearGradient id="scoreGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#988EF2" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="#988EF2" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path d={`${pathD} L ${(PAD + (data.length - 1) * stepX).toFixed(1)} ${H - PAD} L ${PAD} ${H - PAD} Z`} fill="url(#scoreGrad)" />
              <path d={pathD} fill="none" stroke="#5A4BBE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              {data.map((p, i) => p.avgScore != null && (
                <circle key={i} cx={PAD + i * stepX} cy={yFor(p.avgScore)} r="2.5" fill="#5A4BBE" />
              ))}
            </svg>
          </div>
        )}

        {/* Delta */}
        {delta != null && (
          <div className="flex flex-col items-end">
            <div className="text-xs" style={{ color: "#6B7280" }}>{t("salesCoach.list.progressionDeltaLabel")}</div>
            <div className="flex items-center gap-1 font-bold text-lg" style={{ color: delta >= 0 ? "#1E7A3C" : "#C0423F" }}>
              {delta >= 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
              {delta > 0
                ? t("salesCoach.list.progressionDeltaUnitPositive", { delta })
                : t("salesCoach.list.progressionDeltaUnit", { delta })}
            </div>
          </div>
        )}
      </div>

      {/* Gate gamification — palier suivant */}
      {totalThisMonth >= 5 && (
        <div className="mt-3 text-xs flex items-center gap-2" style={{ color: "#5A4BBE" }}>
          <Sparkles className="h-3.5 w-3.5" />
          <span>{t("salesCoach.list.progressionGamification")}</span>
        </div>
      )}
    </Card>
  );
}

function StatCard({ label, value, icon, color }: { label: string; value: string; icon: React.ReactNode; color: string }) {
  return (
    <Card className="p-4 flex items-center gap-3">
      <div className="h-10 w-10 rounded-full flex items-center justify-center text-white shrink-0" style={{ background: color }}>
        {icon}
      </div>
      <div className="min-w-0">
        <div className="text-xs" style={{ color: "#6B7280" }}>{label}</div>
        <div className="font-bold text-lg" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>{value}</div>
      </div>
    </Card>
  );
}

function FilterSelect({
  label, value, onChange, options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <div className="min-w-0">
      <Label className="text-xs font-medium truncate block" style={{ color: "#4B5573" }}>{label}</Label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full mt-1 rounded-md border bg-white px-3 py-2 text-sm h-10"
        style={{ borderColor: "#D1D5DB", color: "#384872" }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

// Affiche les 3 métriques speech compactes (talk-ratio, vitesse, questions ouvertes)
// avec code couleur pour signaler les zones d'alerte.
function renderSpeechCell(r: Row, t: TFn): React.ReactNode {
  if (!r.speechMetrics || r.analysisStatus !== "completed") {
    return <span className="text-xs" style={{ color: "#D1D5DB" }}>{t("salesCoach.list.dash")}</span>;
  }
  const { talkRatioCommercialPct, speechRateCommercialWpm, openQuestionsCount } = r.speechMetrics;
  // Talk-ratio idéal commercial ~30-45% (vente consultative). Alerte si >55% ou <20%.
  const talkAlert = talkRatioCommercialPct > 55 || talkRatioCommercialPct < 20;
  // Vitesse confortable 130-160 wpm. Alerte si >180 (trop rapide) ou <100 (mou).
  const speedAlert = speechRateCommercialWpm > 180 || speechRateCommercialWpm < 100;
  // Questions ouvertes : 5+ sur un appel discovery c'est bien. Alerte si 0-1.
  const questionsAlert = openQuestionsCount <= 1;

  return (
    <div className="text-xs space-y-0.5" style={{ color: "#4B5573" }}>
      <div className="flex items-center gap-1" style={{ color: talkAlert ? "#C0423F" : "#4B5573" }}>
        <span title={t("salesCoach.list.talkRatioTooltip")}>🎤</span>
        <span className="font-mono">{talkRatioCommercialPct}%</span>
      </div>
      <div className="flex items-center gap-1" style={{ color: speedAlert ? "#C0423F" : "#4B5573" }}>
        <span title={t("salesCoach.list.speechRateTooltip")}>⚡</span>
        <span className="font-mono">{t("salesCoach.list.speechRateUnit", { count: speechRateCommercialWpm })}</span>
      </div>
      <div className="flex items-center gap-1" style={{ color: questionsAlert ? "#926B00" : "#1E7A3C" }}>
        <span title={t("salesCoach.list.openQuestionsTooltip")}>❓</span>
        <span className="font-mono">{openQuestionsCount}</span>
      </div>
    </div>
  );
}

function renderScoreCell(r: Row, t: TFn): React.ReactNode {
  if (r.analysisStatus === "completed" && r.scoreGlobal != null) {
    const { bg, text, label } = scoreTierConfig(r.scoreTier, t);
    return (
      <div className="flex items-center gap-2">
        <div className="text-base font-bold" style={{ color: text }}>{r.scoreGlobal}</div>
        <Badge style={{ background: bg, color: text, fontSize: "10px" }}>{label}</Badge>
      </div>
    );
  }
  if (r.analysisStatus === "in_progress" || r.analysisStatus === "pending") {
    return (
      <div className="flex items-center gap-1 text-xs" style={{ color: "#4B5573" }}>
        <Loader2 className="h-3 w-3 animate-spin" /> {t("salesCoach.list.scoreInProgress")}
      </div>
    );
  }
  if (r.analysisStatus === "failed") {
    return (
      <div className="flex items-center gap-1 text-xs" style={{ color: "#C0423F" }}>
        <AlertCircle className="h-3 w-3" /> {t("salesCoach.list.scoreFailed")}
      </div>
    );
  }
  return <span className="text-xs" style={{ color: "#9CA3AF" }}>{t("salesCoach.list.dash")}</span>;
}

// V3 — Couleurs du badge méthodo selon couverture (% covered/total).
function methoCoverageBg(covered: number, total: number): string {
  const pct = total > 0 ? covered / total : 0;
  if (pct >= 0.75) return "#D7F0E0";  // vert pâle
  if (pct >= 0.5)  return "#DDEEFF";  // bleu pâle
  if (pct >= 0.25) return "#FFF1C7";  // jaune pâle
  return "#FFE0DE";                    // rouge pâle
}
function methoCoverageColor(covered: number, total: number): string {
  const pct = total > 0 ? covered / total : 0;
  if (pct >= 0.75) return "#1E7A3C";
  if (pct >= 0.5)  return "#384872";
  if (pct >= 0.25) return "#926B00";
  return "#C0423F";
}

function scoreTierConfig(tier: string | null, t: TFn): { bg: string; text: string; label: string } {
  switch (tier) {
    case "excellent": return { bg: "#D7F0E0", text: "#1E7A3C", label: t("salesCoach.list.tierExcellent") };
    case "good": return { bg: "#DDEEFF", text: "#384872", label: t("salesCoach.list.tierGood") };
    case "needs_improvement": return { bg: "#FFF1C7", text: "#926B00", label: t("salesCoach.list.tierNeedsImprovement") };
    case "insufficient": return { bg: "#FFE0DE", text: "#C0423F", label: t("salesCoach.list.tierInsufficient") };
    default: return { bg: "#F3F4F6", text: "#6B7280", label: t("salesCoach.list.dash") };
  }
}

type TFn = (key: string, opts?: Record<string, unknown>) => string;

function formatRelativeDate(iso: string, t: TFn, lang: string): string {
  try {
    const d = new Date(iso);
    const days = Math.floor((Date.now() - d.getTime()) / 86_400_000);
    if (days === 0) return t("salesCoach.list.dateToday");
    if (days === 1) return t("salesCoach.list.dateYesterday");
    if (days < 7) return t("salesCoach.list.dateDaysAgo", { count: days });
    const locale = lang === "fr" ? "fr-FR" : "en-GB";
    return d.toLocaleDateString(locale, { day: "2-digit", month: "short", year: days > 365 ? "numeric" : undefined });
  } catch {
    return iso;
  }
}

function formatDuration(seconds: number, t: TFn): string {
  const min = Math.floor(seconds / 60);
  const sec = seconds % 60;
  if (min === 0) return t("salesCoach.list.durationSeconds", { seconds: sec });
  if (sec === 0) return t("salesCoach.list.durationMinutes", { minutes: min });
  return t("salesCoach.list.durationMinutesSeconds", { minutes: min, seconds: sec });
}
